/**
 * TeamJoin — /team/join/:token
 *
 * Landing page for a team-invite link. Public route so the invitee can
 * see the team name before signing in; hitting Accept requires them to
 * be logged in with the invited email.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, LogIn, Check, X } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const TeamJoin = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, API } = useContext(AuthContext);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/team/invite-lookup/${token}`);
        setInvite(data);
      } catch (_) {
        setInvite({ found: false });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await axios.post(`${API}/team/accept/${token}`);
      toast.success(`Joined "${data.team_name}"!`);
      navigate('/team');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not accept invite.');
    } finally {
      setAccepting(false);
    }
  };

  const emailMatches = isAuthenticated && user && invite?.email &&
    user.email?.toLowerCase() === invite.email.toLowerCase();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-lg w-full" data-testid="team-join-page">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Team invite</div>
                <CardTitle>Freeze Dry Academy</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && <p className="text-sm text-muted-foreground">Looking up your invite…</p>}

            {!loading && !invite?.found && (
              <div data-testid="invite-not-found">
                <p className="text-sm text-foreground">This invite is invalid or has been revoked.</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Ask the team owner to send a fresh link, or head to the{' '}
                  <Link to="/" className="underline font-bold">home page</Link>.
                </p>
              </div>
            )}

            {!loading && invite?.found && invite.status !== 'pending' && (
              <p className="text-sm text-red-600" data-testid="invite-expired">
                This invite has already been {invite.status}.
              </p>
            )}

            {!loading && invite?.found && invite.status === 'pending' && (
              <>
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/40">
                  <div className="text-xs text-muted-foreground">You&rsquo;ve been invited to join</div>
                  <div className="font-heading text-2xl font-black text-foreground mt-1" data-testid="invite-team-name">{invite.team_name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Role: <strong className="uppercase">{invite.role}</strong> · for{' '}
                    <strong>{invite.email}</strong>
                  </div>
                </div>

                {!isAuthenticated && (
                  <Button
                    onClick={() => navigate(`/login?next=/team/join/${token}`)}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="signin-to-accept-btn"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign in as {invite.email} to accept
                  </Button>
                )}

                {isAuthenticated && !emailMatches && (
                  <div className="text-xs text-red-600 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-300/60">
                    You&rsquo;re signed in as <strong>{user?.email}</strong>. This invite was sent
                    to <strong>{invite.email}</strong>. Log out and sign back in with the
                    invited email to accept.
                  </div>
                )}

                {isAuthenticated && emailMatches && (
                  <Button
                    onClick={accept}
                    disabled={accepting}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="accept-invite-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {accepting ? 'Joining…' : `Accept and join ${invite.team_name}`}
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeamJoin;
